import { FormControlLabel, Switch, type SwitchProps, styled, type Theme, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import type React from "react";
import type { FC, InputHTMLAttributes, ReactNode } from "react";

import { INK, INK_BLACK, NEUTRAL, STATUS_BADGE, SURFACE } from "@/theme/tokens";

type labelPlacement = "end" | "start" | "top" | "bottom";

type Props = {
	checked: boolean;
	onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
	label?: string;
	/** Accessible name when there is no visible `label`. */
	ariaLabel?: string;
	labelPlacement?: labelPlacement;
	disabled?: boolean;
	labelWrap?: boolean;
	loading?: boolean;
	dataTestId?: string;
	labelFontSize?: string | number;
	tooltip?: ReactNode;
	/**
	 * Marks the switch as the control a walkthrough's Next presses (`data-walkthrough-reveal`). Lands on the
	 * native input, so the tour's `.click()` toggles it exactly like a user's click. Additive; unset for
	 * every other caller.
	 */
	"data-walkthrough-reveal"?: string;
};

const switchHeight = 22;

const IOSSwitch = styled((props: SwitchProps & { loading?: boolean }) => {
	const { loading, ...other } = props;
	return <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...other} />;
})(({ theme, loading }) => ({
	width: 42,
	height: switchHeight,
	padding: 0,
	"& .MuiSwitch-switchBase": {
		padding: 0,
		margin: 2,
		color: STATUS_BADGE.BRAND_FALLBACK,
		backgroundColor: alpha(theme.palette.primary.main, 0.2),
		transitionDuration: "200ms",
		"& + .MuiSwitch-track": {
			backgroundColor: alpha(theme.palette.primary.main, 0.2),
			opacity: 1,
			border: "1px solid #50527780",
		},
		"&.Mui-checked": {
			transform: `translateX(${switchHeight - 2}px)`,
			color: SURFACE,
			"& + .MuiSwitch-track": {
				backgroundColor: STATUS_BADGE.BRAND_FALLBACK,
				opacity: 1,
				border: "1px solid #50527780",
				...theme.applyStyles("dark", {
					backgroundColor: STATUS_BADGE.BRAND_FALLBACK,
				}),
			},
			"&.Mui-disabled + .MuiSwitch-track": {
				border: 0,
				opacity: 0.5,
			},
		},
		"&.Mui-focusVisible .MuiSwitch-thumb": {
			color: STATUS_BADGE.BRAND_FALLBACK,
			border: `6px solid ${SURFACE}`,
		},
		"&.Mui-disabled .MuiSwitch-thumb": {
			color: theme.palette.grey[100],
			...theme.applyStyles("dark", {
				color: theme.palette.grey[600],
			}),
		},
		"&.Mui-disabled + .MuiSwitch-track": {
			opacity: 0.7,
			...theme.applyStyles("dark", {
				opacity: 0.3,
			}),
		},
	},
	"@keyframes thumbSpinner": {
		"0%": { transform: "translate(-50%, -50%) rotate(0deg)" },
		"100%": { transform: "translate(-50%, -50%) rotate(360deg)" },
	},
	"& .MuiSwitch-thumb": {
		boxSizing: "border-box",
		width: switchHeight * 0.75,
		height: switchHeight * 0.75,
		...(loading && {
			"&::after": {
				content: '""',
				position: "absolute",
				top: "50%",
				left: "50%",
				width: 10,
				height: 10,
				borderRadius: "50%",
				border: `2px solid ${alpha(INK_BLACK, 0.2)}`, // ring
				borderTopColor: STATUS_BADGE.LILAC_INK, // spinner color
				animation: "thumbSpinner 0.8s linear infinite",
				boxSizing: "border-box",
				transform: "translate(-50%, -50%)",
			},
		}),
	},
	"& .MuiSwitch-track": {
		borderRadius: switchHeight / 2,
		backgroundColor: NEUTRAL[200],
		opacity: 1,
		transition: theme.transitions.create(["background-color"], {
			duration: 500,
		}),
		...theme.applyStyles("dark", {
			backgroundColor: NEUTRAL[800],
		}),
	},
}));

const labelPlacementToMargin: Record<labelPlacement, SystemStyleObject<Theme>> = {
	end: {
		pl: 1,
	},
	start: {
		pr: 1,
	},
	top: {
		pt: 1,
	},
	bottom: {
		pb: 1,
	},
};

/**
 * SystemSwitch component is a styled switch with a label.
 * @param checked
 * @param labelPlacement
 * @param label
 * @param ariaLabel - accessible name when there is no visible label
 * @param onChange
 * @param disabled
 * @param labelWrap - whether the label should wrap or not
 * @param loading - whether the switch is in loading state
 */
export const SystemSwitch: FC<Props> = ({
	checked,
	labelPlacement = "end",
	label,
	ariaLabel,
	onChange,
	disabled,
	labelWrap = false,
	loading,
	dataTestId,
	labelFontSize,
	tooltip,
	"data-walkthrough-reveal": walkthroughReveal,
}) => {
	// A named const, not an inline literal: TypeScript rejects a `data-*` key on a fresh object literal typed
	// as the slot's props, while the DOM takes it as-is.
	const inputProps: InputHTMLAttributes<HTMLInputElement> & { "data-walkthrough-reveal"?: string } = {
		"aria-label": ariaLabel,
		"data-walkthrough-reveal": walkthroughReveal,
	};
	const control = (
		<FormControlLabel
			data-testid={dataTestId}
			sx={{
				marginLeft: "0 !important",
				marginRight: "0 !important",
			}}
			control={<IOSSwitch checked={checked} onChange={onChange} disabled={disabled || loading} loading={loading} slotProps={{ input: inputProps }} />}
			label={
				<Typography
					sx={[
						label ? labelPlacementToMargin[labelPlacement] : {},
						(theme) => ({
							fontWeight: 500,
							fontSize: labelFontSize ?? theme.typography.body2.fontSize,
							lineHeight: "100%",
							color: alpha(INK.SECONDARY, 0.5),
							whiteSpace: labelWrap ? "auto" : "nowrap",
						}),
					]}
				>
					{label}
				</Typography>
			}
			labelPlacement={label ? labelPlacement : undefined}
		/>
	);

	return tooltip ? <Tooltip title={tooltip}>{control}</Tooltip> : control;
};
